const toDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const usedQuotaToday = (timestamp) => {
  const date = toDate(timestamp);
  if (!date) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

export const formatPlanLabel = (rawLabel) => {
  if (!rawLabel || typeof rawLabel !== 'string') return null;
  const lower = rawLabel.toLowerCase().trim();
  
  // Map des noms de plans vers leur affichage formaté
  const planDisplayNames = {
    'muzo_plus': 'Muzo+',
    'muzo+': 'Muzo+',
    'muzoplus': 'Muzo+',
    'muzo_pro': 'Muzo+',
    'muzo pro': 'Muzo+',
    'muzopro': 'Muzo+',
    'pro': 'Muzo+',
    'free': 'Gratuit',
    'gratuit': 'Gratuit',
  };
  
  return planDisplayNames[lower] || rawLabel;
};

const normalizePlanLabel = (plan = {}, saas = {}) => {
  const rawLabel = (
    plan.displayName ||
    plan.name ||
    plan.planId ||
    saas.subscription_plan_label ||
    saas.subscription_plan_id ||
    'Gratuit'
  );
  return formatPlanLabel(rawLabel) || 'Gratuit';
};

const inferPlanType = (plan = {}, saas = {}, label = 'Gratuit') => {
  if (plan.type) return plan.type;
  if (saas.subscription_plan_type) return saas.subscription_plan_type;
  const normalized = typeof label === 'string' ? label.toLowerCase() : '';
  if (normalized.includes('pro') || normalized.includes('muzo+')) return 'pro';
  return 'free';
};

const normalizePriceValue = (value) => {
  if (value == null) return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  return numeric > 50 ? numeric / 100 : numeric;
};

export const getSubscriptionSummary = (userProfile = null) => {
  const plan = userProfile?.plan || {};
  const saas = userProfile?.saas_usage || {};
  const tracking = userProfile?.tracking || {};
  const usage = userProfile?.usage || {};

  const planLabel = normalizePlanLabel(plan, saas);
  const basePlanType = inferPlanType(plan, saas, planLabel);

  const subscriptionStatus =
    plan.status ||
    saas.subscription_status ||
    (saas.subscription_active ? 'active' : 'inactive');

  const isActive =
    subscriptionStatus === 'active' ||
    subscriptionStatus === 'trialing' ||
    Boolean(plan.is_active || saas.subscription_active);

  const planType = isActive && basePlanType === 'free' ? 'pro' : basePlanType;

  const monthlyCredits =
    plan.monthlyCredits ??
    plan.monthly_credits ??
    saas.monthly_credits ??
    saas.credits_monthly ??
    null;

  const nextBilling =
    plan.nextBilling ||
    plan.next_billing ||
    saas.subscription_renews_at ||
    saas.next_billing_at ||
    null;

  const lastFreeDownload =
    usage.lastFreeNowatermarkAt ||
    saas.quota_no_watermark_last_use ||
    tracking.last_free_download_at ||
    saas.last_free_download_at ||
    null;

  const cycleRemaining =
    plan.creditsCycleRemaining ??
    saas.credits_cycle_remaining ??
    saas.credits_monthly_left ??
    saas.monthly_credits_remaining ??
    null;

  let creditsUsedThisCycle =
    plan.creditsCycleUsed ?? saas.credits_cycle_used ?? saas.credits_monthly_used ?? null;

  if (creditsUsedThisCycle == null && monthlyCredits != null && cycleRemaining != null) {
    creditsUsedThisCycle = Math.max(0, monthlyCredits - cycleRemaining);
  }

  const monthlyPrice =
    normalizePriceValue(
      plan.monthlyPrice ??
        plan.price ??
        plan.amount ??
        saas.subscription_amount ??
        saas.monthly_price
    ) ?? null;

  const isUnlimited =
    Boolean(plan.tier === 'unlimited' || plan.is_unlimited || saas.subscription_tier === 'unlimited');

  return {
    planLabel: planLabel || 'Gratuit',
    planType,
    subscriptionStatus: subscriptionStatus || (isActive ? 'active' : 'inactive'),
    isActive,
    isUnlimited,
    monthlyCredits,
    creditsUsedThisCycle,
    monthlyPrice,
    nextBilling,
    lastFreeDownload,
  };
};

/**
 * Retourne le badge pour le téléchargement gratuit
 * @param {Object} summary - Le résumé de l'abonnement
 * @param {string} countdownText - Texte du countdown optionnel (ex: "12h 34m 56s")
 * @returns {Object|null} Badge avec tone, text, et countdown optionnel
 */
export const getFreeDownloadBadge = (summary, countdownText = null) => {
  if (!summary) return null;
  if (summary.planType !== 'free') {
    return {
      tone: 'info',
      text: 'Illimité sans watermark (Muzo+)',
    };
  }

  const usedToday = usedQuotaToday(summary.lastFreeDownload);
  if (usedToday) {
    return {
      tone: 'warning',
      text: 'Nouveau téléchargement gratuit dans',
      countdown: countdownText || null,
    };
  }

  return {
    tone: 'success',
    text: '1 téléchargement gratuit disponible',
  };
};
